import type { CharacterVoiceSynthesisMetrics } from '@proj-airi/stage-shared/character-voice'
import type { SpeechProvider } from '@xsai-ext/providers/utils'

import { defineInvoke } from '@moeru/eventa'
import { createContext } from '@moeru/eventa/adapters/electron/renderer'
import { errorMessageFrom } from '@moeru/std'
import { isElectronWindow, isStageTamagotchi } from '@proj-airi/stage-shared'
import {
  characterVoiceDefaultVoiceId,
  characterVoiceModelId,
  characterVoices,
  electronCharacterVoiceEnsureLoaded,
  electronCharacterVoicePrepare,
  electronCharacterVoiceSynthesize,
} from '@proj-airi/stage-shared/character-voice'
import { getActivePinia } from 'pinia'
import { z } from 'zod'

import { useVelisLatencyStore } from '../../../../stores/velis-latency'
import { defineProvider } from '../registry'

function recordCharacterVoiceLatency(turnId: string | undefined, metrics: CharacterVoiceSynthesisMetrics) {
  if (!turnId || !getActivePinia())
    return
  try {
    const latency = useVelisLatencyStore()
    latency.recordEvent(turnId, 'sidecar-ready', metrics.sidecarReadyAt)
    latency.recordEvent(turnId, 'voice-weights-ready', metrics.weightsReadyAt)
    latency.recordEvent(turnId, 'audio-ready', metrics.audioReadyAt)
    const warmPath = metrics.warmPath
    latency.recordFacts(turnId, {
      voiceStart: metrics.coldStart ? 'cold' : metrics.voiceSwitch ? 'voice-switch' : 'warm',
      warmPath,
      measured: {
        ...(metrics.coldStart ? { ttsLoadMs: metrics.loadMs } : {}),
        ...(metrics.voiceSwitch ? { voiceSwitchMs: metrics.switchMs } : {}),
        synthesisMs: metrics.synthesisMs,
      },
    })
    latency.recordVoiceWarm({
      phase: 'ready',
      voiceId: metrics.voiceId,
      weightsReady: true,
      warmPath,
    })
  }
  catch {
    // A latency write must not replace the speech result.
  }
}

function bytesFromBase64(value: string) {
  const binary = atob(value)
  const bytes = new Uint8Array(binary.length)
  for (let index = 0; index < binary.length; index++)
    bytes[index] = binary.charCodeAt(index)
  return bytes
}

function electronInvokes() {
  if (typeof window === 'undefined' || !isElectronWindow(window))
    throw new Error('Character voice requires the Electron desktop app.')

  const { context } = createContext(window.electron.ipcRenderer)
  return {
    ensureLoaded: defineInvoke(context, electronCharacterVoiceEnsureLoaded),
    prepare: defineInvoke(context, electronCharacterVoicePrepare),
    synthesize: defineInvoke(context, electronCharacterVoiceSynthesize),
  }
}

/** Loads the sidecar and the selected voice weights. The caller handles failure. */
export async function prepareCharacterVoice(voiceId: string) {
  return await electronInvokes().prepare({ voiceId })
}

export const providerCharacterVoice = defineProvider({
  id: 'character-voice',
  name: 'Character Voice (Local)',
  nameLocalize: ({ t }) => t('settings.pages.providers.provider.character-voice.title'),
  description: 'Local Japanese character speech on this computer.',
  descriptionLocalize: ({ t }) => t('settings.pages.providers.provider.character-voice.description'),
  tasks: ['text-to-speech'],
  icon: 'i-lobe-icons:speaker',
  requiresCredentials: false,
  isAvailableBy: () => isStageTamagotchi(),
  createProviderConfig: () => z.object({
    model: z.string().default(characterVoiceModelId),
    voiceId: z.string().default(characterVoiceDefaultVoiceId),
  }),
  createProvider(): SpeechProvider {
    const invokes = electronInvokes()
    return {
      speech: () => ({
        baseURL: 'http://character-voice/v1/',
        model: characterVoiceModelId,
        fetch: async (_input: RequestInfo | URL, init?: RequestInit) => {
          if (!init?.body || typeof init.body !== 'string')
            throw new Error('Invalid request body')
          const body = JSON.parse(init.body) as { input?: string, voice?: string, turn_id?: string }
          if (!body.voice)
            throw new Error('Voice parameter is required')
          try {
            const result = await invokes.synthesize({
              text: body.input ?? '',
              voiceId: body.voice,
              ...(body.turn_id ? { turnId: body.turn_id } : {}),
            })
            recordCharacterVoiceLatency(body.turn_id, result.metrics)
            return new Response(bytesFromBase64(result.wavBase64), {
              status: 200,
              headers: { 'Content-Type': 'audio/wav' },
            })
          }
          catch (error) {
            throw new Error(errorMessageFrom(error) || 'Character voice synthesis failed.')
          }
        },
      }),
    }
  },
  validationRequiredWhen: () => false,
  extraMethods: {
    listModels: async () => [{
      id: characterVoiceModelId,
      name: 'GPT-SoVITS',
      provider: 'character-voice',
    }],
    loadModel: async () => {
      await electronInvokes().ensureLoaded()
    },
    listVoices: async () => characterVoices
      .filter(voice => voice.id === 'natsume-casual' || voice.id === 'atri')
      .map(voice => ({
        id: voice.id,
        name: voice.id === 'atri' ? 'ATRI' : '四季ナツメ',
        provider: 'character-voice',
        languages: [{ code: voice.languageCode, title: voice.languageTitle }],
        gender: voice.gender,
      })),
  },
})
